import type { Entry } from '@ozone/shared';
import { currentMonthKey } from './compose';
import type { Me } from './TeamView';
import { simulatedContributorMe } from './previewIdentity';

export interface DevOverride {
  me: Me;
  entries: Entry[];
}

/**
 * AOZONE-91 dev/preview-only override. TeamView is otherwise reachable only by
 * a real bridge-linked non-admin, and locally there is no Lambda/proxy to log
 * in against. This lets a developer render TeamView (or the admin UI) with a
 * stubbed `/api/auth/me` and seed entries.
 *
 * It is COMPILED OUT of production bundles: `import.meta.env.DEV` is statically
 * `false` in a prod `vite build`, so this whole branch dead-code-eliminates and
 * `?devview=` has no effect in production — the SPA falls back to the normal
 * (redirect-to-login) path. Never a security control; UX only.
 *
 * Active in DEV when either `?devview=contributor|admin` is present or the
 * `VITE_DEV_TEAMVIEW` env flag is set (which defaults the role to contributor).
 */
export function getDevOverride(search: string, now?: Date): DevOverride | null {
  if (!import.meta.env.DEV) return null;
  const param = new URLSearchParams(search).get('devview');
  const role = param ?? (import.meta.env.VITE_DEV_TEAMVIEW ? 'contributor' : null);
  if (role !== 'contributor' && role !== 'admin') return null;
  return {
    // AOZONE-115: the contributor identity is built through the same seam the
    // shipped preview feature uses, so the two cannot drift.
    //
    // The admin gets NO slackUserId. A real admin has none
    // (apps/admin-api/src/session.ts:52-54 assigns one only for non-admins);
    // handing the dev admin a fake `U_DEV_ME` — as this did until AOZONE-115 —
    // made `?devview=admin` unable to reproduce the own/all bug the preview
    // feature exists to prevent, which is exactly the identity you need faithful
    // when using this seam as browser evidence.
    me:
      role === 'contributor'
        ? simulatedContributorMe('U_DEV_ME', 'dev-contributor@qumulo.com')
        : { email: 'dev-admin@qumulo.com', role: 'admin' },
    entries: seedEntries(now),
  };
}

/**
 * A small spread of seed entries so the view isn't empty when there's no API.
 *
 * AOZONE-114: the spread deliberately covers all three action-item states —
 * `dev-1` has a normal action item, `dev-2` has a ~500-character one (the
 * documented worst case for the field), and `dev-3` (Market Signals, where the
 * field is optional) has none. Keep that coverage: rendering the action item was
 * dropped once precisely because nothing exercised it visually.
 *
 * HOW TO SEE ALL THREE: `dev-3` belongs to `U_TEAMMATE`, not to the previewing
 * caller, so `?devview=contributor` opens in own-entries-only mode and shows
 * only `dev-1` and `dev-2`. You must tick "Show all entries" to reach the
 * no-action-item case. That split is deliberate — it is also what makes this
 * seed able to exercise own-vs-all in both directions, which AOZONE-115's
 * preview evidence depends on — but the earlier version of this comment claimed
 * all three were visible on open, which is false and would mislead the next
 * person reproducing the evidence.
 */
function seedEntries(now?: Date): Entry[] {
  const period = currentMonthKey(now);
  return [
    {
      entryId: 'dev-1',
      submitterId: 'U_DEV_ME',
      submitterAlias: 'you (dev)',
      createdAt: `${period}-05T12:00:00.000Z`,
      mbrPeriod: period,
      entryCategory: 'Top Customer Engagements',
      customerName: 'Contoso',
      dealSizeAmount: 1500000,
      dealSizeType: 'ARR',
      title: 'Renewal in flight',
      narrativeBody:
        'Multi-year renewal conversation progressing. Champion aligned; procurement engaged. '.repeat(
          3,
        ),
      actionItem: 'Get the revised sizing model in front of procurement before month end.',
    },
    {
      entryId: 'dev-2',
      submitterId: 'U_DEV_ME',
      submitterAlias: 'you (dev)',
      createdAt: `${period}-06T12:00:00.000Z`,
      mbrPeriod: period,
      entryCategory: 'BD Activities',
      partnerOrCloud: 'AWS',
      title: 'Co-sell motion kicked off',
      narrativeBody: 'Kicked off a co-sell motion with the AWS partner team for two accounts.',
      // ~500 chars: the worst case the Action row layout has to absorb.
      actionItem:
        'Schedule the joint architecture review with the platform team before the end of the month, then circulate the revised sizing model to procurement and the finance team for sign-off. Confirm the PoC hardware allocation with the datacentre team, get the security questionnaire back from InfoSec, and lock the migration window with the storage admins. Follow up with the reseller on the paper-out timeline, chase the signed NDA from legal, and open the account-mapping spreadsheet to the partner alliance lead by Friday.',
    },
    {
      entryId: 'dev-3',
      submitterId: 'U_TEAMMATE',
      submitterAlias: 'teammate',
      createdAt: `${period}-07T12:00:00.000Z`,
      mbrPeriod: period,
      entryCategory: 'Market Signals',
      title: 'Competitor pricing shift',
      narrativeBody: 'Two prospects independently mentioned aggressive competitor discounting.',
    },
  ];
}
